import { useMemo, useState } from "react";
import {
    ScheduleRecognitionOverrides,
    ScheduleTemplateResponse,
} from "@common/schemas/domains/accounting/scheduleTemplate.schema";
import { ScheduleTermUnit } from "@common/types/domains/accounting/schedule";
import {
    buildScheduleDraftOverrides,
    createScheduleDraft,
    getScheduleDraftError,
    resolveScheduleDraft,
    ResolvedScheduleDraft,
    ScheduleDraft,
} from "./scheduleDraft";
import { resolvePolicyAccountIds } from "./scheduleManualApplyHelpers";

interface ScheduleDraftStart {
    amount: number;
    termStart: string;
    specificDate: string;
    /** The source's own day, when the schedule is applied to one. */
    sourceDate?: string | null;
    /** The term the schedule runs on today: the template's or its own. */
    term: { length: number; unit: ScheduleTermUnit };
}

interface EditedDraft {
    templateId: string;
    entityId: number | null;
    draft: ScheduleDraft;
}

export interface ScheduleDraftState {
    draft: ScheduleDraft | null;
    setDraft: (draft: ScheduleDraft) => void;
    resolved: ResolvedScheduleDraft | null;
    error: string | null;
    overrides: ScheduleRecognitionOverrides;
}

/**
 * The modal's editable schedule: the template's rows until the user edits
 * them. A new entity swaps in the policy's accounts for it and keeps the rows.
 */
export function useScheduleDraftState(options: {
    template: ScheduleTemplateResponse | undefined;
    entityId: number | null;
    start: ScheduleDraftStart;
    /** A source-backed schedule spreads exactly the source amount. */
    fixedAmount?: number;
    /** The account the source's own lines sit in, which recognition defaults to. */
    sourceRecognitionAccountId?: string | null;
}): ScheduleDraftState {
    const { template, entityId, start, fixedAmount } = options;
    const sourceRecognitionAccountId =
        options.sourceRecognitionAccountId ?? null;
    const [edited, setEdited] = useState<EditedDraft | null>(null);
    const { amount, termStart, specificDate } = start;
    const sourceDate = start.sourceDate ?? null;
    const termLength = start.term.length;
    const termUnit = start.term.unit;

    const draft = useMemo(() => {
        if (!template) {
            return null;
        }
        if (edited?.templateId !== template.id) {
            return createScheduleDraft({
                template,
                entityId,
                amount,
                termStart,
                specificDate,
                sourceDate,
                sourceRecognitionAccountId,
                term: { length: termLength, unit: termUnit },
            });
        }
        if (edited.entityId === entityId) {
            return edited.draft;
        }
        const accountIds = resolvePolicyAccountIds({
            accountRows: template.accountRows,
            entityId,
        });
        return {
            ...edited.draft,
            holdingAccountId: accountIds?.holdingAccountId ?? null,
            recognitionAccountId:
                sourceRecognitionAccountId ??
                accountIds?.recognitionAccountId ??
                null,
        };
    }, [
        amount,
        edited,
        entityId,
        sourceDate,
        sourceRecognitionAccountId,
        specificDate,
        template,
        termLength,
        termStart,
        termUnit,
    ]);

    const resolved = useMemo(
        () => (draft ? resolveScheduleDraft(draft) : null),
        [draft],
    );
    const error = useMemo(
        () => (draft ? getScheduleDraftError({ draft, fixedAmount }) : null),
        [draft, fixedAmount],
    );
    const overrides = useMemo(
        () =>
            draft && template
                ? buildScheduleDraftOverrides({
                      draft,
                      template,
                      entityId,
                      sourceDate,
                      sourceRecognitionAccountId,
                      term: { length: termLength, unit: termUnit },
                  })
                : {},
        [
            draft,
            entityId,
            sourceDate,
            sourceRecognitionAccountId,
            template,
            termLength,
            termUnit,
        ],
    );

    return {
        draft,
        setDraft: (nextDraft) => {
            if (template) {
                setEdited({
                    templateId: template.id,
                    entityId,
                    draft: nextDraft,
                });
            }
        },
        resolved,
        error,
        overrides,
    };
}
